import { Client, StompSubscription } from '@stomp/stompjs';
import { Actions as AppActions } from '../actions/constants/Actions';
import { Actions as StreamApiActions, Channel } from '../actions/streamApi';
import { Dispatch } from 'redux';
import {
  CHANNEL_CONFIG,
  ChannelConfig,
  WEBSOCKET_ENDPOINTS,
} from '../config/websocketChannels';

let channelSubscriptions: Map<
  string,
  { subscription: StompSubscription; data: any }
> = new Map();
let pendingSubscriptions: Map<string, any> = new Map();

let stompClient: Client | null = null;
let accessTokenClosure: string | null = null;

const streamStompMiddleware =
  (store: { getState; dispatch: Dispatch }) =>
  (next: (action: any) => any) =>
  (action: any) => {
    if (
      action.type === AppActions.ACCESS_TOKEN_UPDATED ||
      action.type === AppActions.RESET_SOCKET_CONNECTION
    ) {
      const { accessToken } = action.payload;
      accessTokenClosure = accessToken || accessTokenClosure;

      cleanupStompMiddleware();
      initializeStompClient(store);
      next(action);
    }

    // Handle subscription actions
    if (
      action.type === StreamApiActions.SEND_MESSAGE &&
      action.data?.channel &&
      action.data?.type === 'SUBSCRIBE'
    ) {
      handleSubscription(store, action.data);
    }

    // Handle unsubscription actions
    if (
      action.type === StreamApiActions.SEND_MESSAGE &&
      action.data?.channel &&
      action.data?.type === 'UNSUBSCRIBE'
    ) {
      handleUnsubscription(store, action.data);
    }

    next(action);
  };

const initializeStompClient = (store: { getState; dispatch: Dispatch }) => {
  // Clear all subscriptions
  channelSubscriptions.clear();

  // Create new STOMP client
  stompClient = new Client({
    brokerURL: WEBSOCKET_ENDPOINTS.STOMP_SERVICE,
    connectHeaders: {
      Authorization: `Bearer ${accessTokenClosure || ''}`,
    },
    reconnectDelay: 5000,
    heartbeatIncoming: 4000,
    heartbeatOutgoing: 4000,
  });

  stompClient.onConnect = () => {
    store.dispatch({
      type: StreamApiActions.CONNECTED,
      payload: { status: 'OK' },
    });
    // Resubscribe to all active subscriptions
    resubscribeToAllTopics(store);

    // Process pending subscriptions
    pendingSubscriptions.forEach((data, subscriptionKey) => {
      const config =
        CHANNEL_CONFIG[data.channel as keyof typeof CHANNEL_CONFIG];
      if (config) {
        subscribeToChannel(store, data, config);
        pendingSubscriptions.delete(subscriptionKey);
      }
    });
  };

  stompClient.onStompError = (frame) => {
    console.error('STOMP error:', frame);
    store.dispatch({
      type: StreamApiActions.ERROR,
      error: frame,
    });
  };

  stompClient.onWebSocketError = (error) => {
    console.error('STOMP WebSocket error:', error);
    store.dispatch({
      type: StreamApiActions.ERROR,
      error,
    });
  };

  stompClient.onWebSocketClose = () => {
    store.dispatch({
      type: StreamApiActions.CLOSED,
      reason: 'STOMP connection closed',
    });
  };

  stompClient.activate();
};

// Subscription handler
const handleSubscription = (
  store: { getState; dispatch: Dispatch },
  data: any
) => {
  const { channel } = data;
  const config = CHANNEL_CONFIG[channel as keyof typeof CHANNEL_CONFIG];

  if (!config) {
    console.warn(`Unknown STOMP channel: ${channel}`);
    return;
  }

  subscribeToChannel(store, data, config);
};

// Unsubscription handler
const handleUnsubscription = (
  store: { getState; dispatch: Dispatch },
  data: any
) => {
  const { channel } = data;
  const config = CHANNEL_CONFIG[channel as keyof typeof CHANNEL_CONFIG];

  if (!config) {
    console.warn(`Unknown STOMP channel: ${channel}`);
    return;
  }

  unsubscribeFromChannel(store, data, config);
};

// Subscription function
const subscribeToChannel = (
  store: { getState; dispatch: Dispatch },
  data: any,
  config: ChannelConfig
) => {
  const { channel } = data;

  // Validate subscription data
  if (!config.validateData(data)) {
    return;
  }

  const subscriptionKey = config.getSubscriptionKey(data);

  if (!stompClient || !stompClient.connected) {
    pendingSubscriptions.set(subscriptionKey, data);
    return;
  }

  // Check if subscription already exists and unsubscribe first
  if (channelSubscriptions.has(subscriptionKey)) {
    const existingSubscription = channelSubscriptions.get(subscriptionKey);
    if (existingSubscription) {
      unsubscribeFromChannel(store, data, config);
    }
  }

  try {
    // Get topic mapping function and call it with appropriate arguments
    const topic = config.getTopic(data);

    const subscription = stompClient.subscribe(
      topic,
      (message) => handleChannelMessage(store, message, data, config),
      {
        Authorization: `Bearer ${accessTokenClosure}`,
      }
    );

    // Store both subscription and original data
    channelSubscriptions.set(subscriptionKey, { subscription, data });

    publishSubscribeMessage(store, data, channel);
  } catch (error) {
    console.error(`Error subscribing to ${channel} via STOMP:`, error);
  }
};

// Unsubscription function
const unsubscribeFromChannel = (
  store: { getState; dispatch: Dispatch },
  data: any,
  config: ChannelConfig
) => {
  const subscriptionKey = config.getSubscriptionKey(data);
  const subscriptionData = channelSubscriptions.get(subscriptionKey);

  if (subscriptionData) {
    subscriptionData.subscription.unsubscribe();
    channelSubscriptions.delete(subscriptionKey);
  }
};

// Message handler
const handleChannelMessage = (
  store: { getState; dispatch: Dispatch },
  message: any,
  originalData: any,
  config: ChannelConfig
) => {
  try {
    const parsedData = JSON.parse(message.body);
    const action = config.processMessage(parsedData, originalData);

    // Check if we should auto-unsubscribe
    if (
      config.shouldAutoUnsubscribe &&
      config.shouldAutoUnsubscribe(parsedData)
    ) {
      unsubscribeFromChannel(store, originalData, config);
    }

    // Dispatch the appropriate action
    store.dispatch(action);
  } catch (error) {
    console.error(
      `Error parsing STOMP ${originalData.channel} message:`,
      error
    );
  }
};

// Publish subscribe message
const publishSubscribeMessage = (
  store: { getState; dispatch: Dispatch },
  data: any,
  channel: Channel
) => {
  const accessToken = store.getState().app.accessToken;
  if (!stompClient || !accessToken) return;

  try {
    const publishTopic = `/app/subscribe`;
    stompClient.publish({
      destination: publishTopic,
      body: JSON.stringify({ ...data, channel }),
      headers: {
        Authorization: `Bearer ${accessToken || accessTokenClosure}`,
      },
    });
  } catch (publishError) {
    console.error('Error publishing subscribe message:', publishError);
  }
};

// Resubscribe to all topics
const resubscribeToAllTopics = (store: { getState; dispatch: Dispatch }) => {
  if (!stompClient?.connected) return;

  // Clear existing subscriptions first
  const subscriptionsToResubscribe = Array.from(channelSubscriptions.values());
  channelSubscriptions.clear();

  // Resubscribe to all stored subscriptions
  subscriptionsToResubscribe.forEach((subscriptionData: any) => {
    const { channel } = subscriptionData.data;
    const config = CHANNEL_CONFIG[channel as keyof typeof CHANNEL_CONFIG];

    if (config) {
      subscribeToChannel(store, subscriptionData.data, config);
    }
  });
};

// Cleanup function
export const cleanupStompMiddleware = () => {
  if (stompClient) {
    stompClient.deactivate();
    stompClient = null;
  }

  channelSubscriptions.clear();
};

export default streamStompMiddleware;
